import { onDocumentReady, requestIdleCallback } from '@theme/utilities';

/**
 * Collection / search hero description "Read More".
 *
 * The description is truncated in JS (not with -webkit-line-clamp) so the control can sit
 * inline right after the ellipsis instead of taking an extra line below the text.
 */

/**
 * Only a fallback: the collapsed height is driven by the --sl-desc-lines custom property in
 * assets/sl-custom.css, which is also what the pre-JS CSS clamp uses. Change the line count
 * there (per breakpoint if needed), never here.
 */
const DEFAULT_COLLAPSED_LINES = 2;
const HEIGHT_TOLERANCE = 2;
const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
const INLINE_CLASS = 'sl-collections-hero__desc--inline-more';
const LABEL_MORE = 'Read More';
const LABEL_LESS = 'Read Less';
const BUTTON_CLASS = 'sl-collections-hero__desc-readmore js-sl-collections-hero-desc-readmore';
const RESIZE_DEBOUNCE = 150;

/**
 * Collects every text node under a node, in document order.
 * @param {Node} root
 * @returns {Text[]}
 */
function textNodesOf(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  /** @type {Text[]} */
  const nodes = [];
  let current = walker.nextNode();

  while (current) {
    nodes.push(/** @type {Text} */ (current));
    current = walker.nextNode();
  }

  return nodes;
}

/**
 * @param {ParentNode & HTMLElement} section
 */
function initCollectionReadMore(section = document.documentElement) {
  const desc = /** @type {HTMLElement | null} */ (section.querySelector('.js-sl-collections-hero-desc'));

  if (!desc || desc.dataset.readmore === 'ready') return;
  desc.dataset.readmore = 'ready';

  // Untouched copy of the rich-text content — every render starts from this.
  const source = document.createDocumentFragment();
  for (const node of Array.from(desc.childNodes)) source.appendChild(node.cloneNode(true));

  const rawText = textNodesOf(source)
    .map((node) => node.nodeValue ?? '')
    .join('');

  if (!rawText.trim()) return;

  /**
   * Content for the collapsed preview. Longer collection descriptions are full SEO blocks that
   * open with an <h2> repeating the page H1 — at heading font size that one element eats the
   * whole height budget, so the preview would show a duplicate title and none of the prose.
   * Leading headings are therefore dropped from the preview only; expanding restores them.
   */
  const previewSource = (() => {
    const fragment = /** @type {DocumentFragment} */ (source.cloneNode(true));

    while (fragment.firstChild) {
      const node = fragment.firstChild;
      const isBlankText = node.nodeType === Node.TEXT_NODE && !node.nodeValue?.trim();
      const isHeading =
        node.nodeType === Node.ELEMENT_NODE && HEADING_TAGS.has(/** @type {Element} */ (node).tagName);

      if (!isBlankText && !isHeading) break;
      node.remove();
    }

    // A description made of nothing but headings still needs something to preview.
    return fragment.textContent?.trim() ? fragment : source.cloneNode(true);
  })();

  const previewText = textNodesOf(previewSource)
    .map((node) => node.nodeValue ?? '')
    .join('');

  // Older markup rendered the control as a sibling — it is inline now.
  const legacyControl = desc.nextElementSibling;
  if (legacyControl?.classList.contains('js-sl-collections-hero-desc-readmore')) legacyControl.remove();

  const button = document.createElement('button');
  button.type = 'button';
  button.className = BUTTON_CLASS;
  button.textContent = LABEL_MORE;
  button.setAttribute('aria-expanded', 'false');

  // INLINE_CLASS (which lifts the CSS height budget) is deliberately NOT added here: adding it
  // at init left a window between "clamp off" and "text truncated" where the full description
  // painted and then visibly collapsed. It is added inside collapse()/expand() instead, in the
  // same task that renders the truncated content.

  let expanded = false;

  /**
   * Height budget for the collapsed state, in px. Re-read on every collapse so a breakpoint
   * change to --sl-desc-lines is picked up on resize.
   */
  const collapsedMaxHeight = () => {
    const styles = getComputedStyle(desc);
    const declaredLines = parseInt(styles.getPropertyValue('--sl-desc-lines'), 10);
    const lines = declaredLines > 0 ? declaredLines : DEFAULT_COLLAPSED_LINES;
    const lineHeight = parseFloat(styles.lineHeight);
    const resolved = Number.isNaN(lineHeight) ? parseFloat(styles.fontSize) * 1.4 : lineHeight;

    return resolved * lines + HEIGHT_TOLERANCE;
  };

  /** Snaps a character count back to the previous word boundary of the preview text. */
  const snapToWord = (limit) => {
    if (limit >= previewText.length) return previewText.length;
    const boundary = previewText.lastIndexOf(' ', limit);

    return boundary > 0 ? boundary : limit;
  };

  const renderFull = () => desc.replaceChildren(source.cloneNode(true));

  /** The "… " (or " ") text node that sits between the text and the control. */
  let controlPrefix = /** @type {Text | null} */ (null);

  /**
   * Appends the control to the end of the last line of text, so it flows inline.
   * @param {string} prefix text inserted right before the control
   */
  const appendControl = (prefix) => {
    const nodes = textNodesOf(desc);
    /** @type {Element} */
    let host = desc;

    for (let index = nodes.length - 1; index >= 0; index--) {
      if (nodes[index].nodeValue?.trim()) {
        host = nodes[index].parentElement ?? desc;
        break;
      }
    }

    controlPrefix = document.createTextNode(prefix);
    host.appendChild(controlPrefix);
    host.appendChild(button);
  };

  /**
   * True when the control renders on the same line as the ellipsis it follows.
   *
   * "word…" is unbreakable, but the space before the control is a break opportunity — so the
   * longest cut that satisfies the height budget can still push "Read More" onto a line of its
   * own, below the ellipsis instead of right after it.
   */
  const isControlOnEllipsisLine = () => {
    if (!controlPrefix?.isConnected) return true;

    const range = document.createRange();
    range.selectNodeContents(controlPrefix);
    const prefixRect = range.getBoundingClientRect();
    const controlRect = button.getBoundingClientRect();

    if (!prefixRect.height || !controlRect.height) return true;

    return Math.abs(prefixRect.top - controlRect.top) <= 1;
  };

  /**
   * Renders the description cut to `limit` characters, followed by "… Read More".
   * @param {number} limit
   */
  const renderTruncated = (limit) => {
    desc.replaceChildren(previewSource.cloneNode(true));

    let remaining = limit;
    /** @type {Text | null} */
    let lastKept = null;

    for (const node of textNodesOf(desc)) {
      const value = node.nodeValue ?? '';

      if (remaining <= 0) {
        node.remove();
        continue;
      }

      if (value.length > remaining) {
        node.nodeValue = value.slice(0, remaining);
        remaining = 0;
      } else {
        remaining -= value.length;
      }

      if (node.nodeValue?.trim()) lastKept = node;
    }

    // Drop wrappers left empty by the cut so they stop taking vertical space.
    for (const element of Array.from(desc.querySelectorAll('*')).reverse()) {
      if (!element.textContent?.trim() && !element.querySelector('img')) element.remove();
    }

    if (lastKept) lastKept.nodeValue = (lastKept.nodeValue ?? '').replace(/[\s.,;:!?…-]+$/u, '');

    appendControl('… ');
  };

  const collapse = () => {
    expanded = false;

    // Budget is read while the CSS clamp is still in force, then the clamp is lifted and the
    // truncated content rendered in this same task — so no frame ever shows the full text.
    const maxHeight = collapsedMaxHeight();

    desc.classList.add(INLINE_CLASS, 'is-collapsed');
    renderFull();

    // Whether a control is needed at all is judged on the complete description, headings included.
    if (desc.scrollHeight <= maxHeight) {
      desc.classList.remove('is-collapsed');
      button.remove();
      return;
    }

    let low = 0;
    let high = previewText.length;
    let best = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      renderTruncated(snapToWord(mid));

      if (desc.scrollHeight <= maxHeight) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    let limit = snapToWord(best);
    renderTruncated(limit);

    // Give back one word at a time until the control shares the ellipsis' line. Each step frees
    // ~40–60px on that line while the control needs ~75px, so this settles in 1–2 iterations;
    // the counter is only a backstop.
    let attempts = 8;
    while (attempts-- > 0 && limit > 0 && !isControlOnEllipsisLine()) {
      const shorter = snapToWord(limit - 1);
      if (shorter === limit) break;

      limit = shorter;
      renderTruncated(limit);
    }

    button.textContent = LABEL_MORE;
    button.setAttribute('aria-expanded', 'false');
  };

  const expand = () => {
    expanded = true;
    desc.classList.add(INLINE_CLASS);
    desc.classList.remove('is-collapsed');
    renderFull();
    appendControl(' ');
    button.textContent = LABEL_LESS;
    button.setAttribute('aria-expanded', 'true');
  };

  button.addEventListener('click', () => {
    try {
      expanded ? collapse() : expand();
    } catch (error) {
      console.error('collection-readmore button click handler failed:', error);
    }
  });

  /** @type {number} */
  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (!expanded) collapse();
    }, RESIZE_DEBOUNCE);
  });

  // Truncate right away so the control is usable early, then re-measure once webfonts have
  // swapped in — TypeKit/Google Fonts are app-injected here and can land seconds after paint,
  // and the first pass would have measured against fallback metrics. Both passes stay inside
  // the 2-line budget, so the re-cut never changes the element's height.
  requestIdleCallback(collapse);

  if (document.fonts && document.fonts.status !== 'loaded') {
    document.fonts.ready.then(() => {
      if (!expanded) requestIdleCallback(collapse);
    });
  }
}

/* -----------------------------------
   Horizon lifecycle integration
----------------------------------- */

// Initial DOM ready
onDocumentReady(() => initCollectionReadMore());
